//src/types/Store/Aircraft.ts

import {
  collection,
  CollectionReference,
  QueryDocumentSnapshot,
  Timestamp,
  type DocumentData,
  type SnapshotOptions
} from 'firebase/firestore'
import { convertTimestampsToDates, FirebaseProperties } from '../Helper/FirebaseProperties'
import { db } from '@/firebase'
import type { Engine } from './Aircraft/Engine'
import { createPropeller, type Propeller, type Hub } from './Aircraft/Prop'
import { createCurrentFinance } from '../Helper/Interfaces'
import type {
  fAAACFTREF,
  fAAEngine,
  fAAMaster,
  ObjectSummary,
  CurrentFinance
} from '../Helper/Interfaces'
import type { DocI } from '../Document'
import { Squawk } from './Aircraft/Squawk'
import { Category, EngineType, type FaaAircraftTypes } from '../Helper/Enums'
import {
  createAdmin,
  createEquipment,
  createInfo,
  createMaintenanceToDate,
  createOwner,
  createRequirements,
  type Admin,
  type InstructorRequirements,
  type Equipment,
  type Info,
  type MaintenanceToDate,
  type Owner,
  type Requirements
} from './Aircraft/aircraftInterfaces'
import { AircraftClass } from '../Account/AccountEnums'
import { FirebaseCollections, FirestorePaths } from '@/utils/firestore/FirestorePaths'

export class Aircraft extends FirebaseProperties {
  numFlights = 0
  engines: Engine[] = []
  props: Propeller[] = []
  hubs: Hub[] = []
  documents: DocI[] = []
  squawks: Squawk[] = []
  maintenanceTechIds: string[] = []

  info: Info = createInfo()
  equipment: Equipment = createEquipment()
  owners: Owner[] = []
  requirements: Requirements | null = null
  cfiRequirements: InstructorRequirements | null = null
  admin: Admin = createAdmin()
  maintenanceToDate: MaintenanceToDate = createMaintenanceToDate()
  finance: CurrentFinance = createCurrentFinance()
  enableEmailNotifications = false
  enablePhoneNotifications = false

  financeLedgerCollection: CollectionReference = collection(db, '/Finance')
  documentsCollection: CollectionReference | null = null
  maintenanceCollection: CollectionReference | null = null

  constructor(data?: Partial<Aircraft>) {
    super()
    Object.assign(this, data)

    if (this.admin.stores.length > 0) {
      this.setStore(this.admin.stores[0])
    }
  }

  setStore(storeId: string) {
    const id = this.firebaseInfo?.__id

    this.thisCollection = FirestorePaths.storeCollection(storeId, FirebaseCollections.Aircraft)

    this.thisDocument = id
      ? FirestorePaths.storeDocument(storeId, FirebaseCollections.Aircraft, id)
      : this.thisCollection

    this.setChildCollections()
  }

  setChildCollections() {
    this.financeLedgerCollection = collection(
      db,
      FirestorePaths.nestedCollection(this.thisDocument, FirebaseCollections.Finance)
    )

    this.documentsCollection = collection(
      db,
      FirestorePaths.nestedCollection(this.thisDocument, FirebaseCollections.Documents)
    )

    this.maintenanceCollection = collection(
      db,
      FirestorePaths.nestedCollection(this.thisDocument, FirebaseCollections.Maintenances)
    )
  }

  getName(): string {
    return this.info.tailNumber
  }

  getId(): string {
    return this.info.tailNumber
  }

  getTailNumber(): string {
    return this.info.tailNumber
  }

  getActive(): boolean {
    return this.admin.isActive
  }

  getSearchables(): string {
    return [
      this.info.tailNumber,
      this.info.make,
      this.info.model,
      this.info.location,
      this.equipment.eltHexCode,
      ...this.owners.map((o) => o.name),
      ...this.owners.map((o) => o.insuranceCo)
    ]
      .filter(Boolean)
      .join(' ')
  }

  getMakeModelString(): string {
    return `${this.info.make} - ${this.info.model}`
  }

  // Category;Class;Exact;MakeModel
  getCurrencyString(): string {
    return [
      this.info.category,
      this.info.class,
      this.info.tailNumber,
      this.getMakeModelString()
    ].join(';')
  }

  getSummary(): ObjectSummary {
    return {
      id: this.firebaseInfo.__id,
      name: this.getName(),
      active: this.admin.isActive
    }
  }

  getIsValidToSave(): boolean {
    return !!(
      this.info.tailNumber &&
      this.info.category &&
      this.info.class &&
      (this.admin.stores.length > 0 || this.admin.isTransient)
    )
  }

  loadFAAMaster(faaMaster: fAAMaster) {
    this.info.tailNumber = faaMaster['N-NUMBER'].trim()
    this.info.serialNumber = faaMaster['SERIAL NUMBER']

    if (faaMaster['YEAR MFR']) {
      this.info.mfrYear = Number(faaMaster['YEAR MFR'])
    }

    if (this.owners.length === 0) this.owners.push(createOwner())
    this.owners[0].name = faaMaster.NAME
    this.equipment.modesCode = faaMaster['MODE S CODE']

    if (faaMaster['EXPIRATION DATE']) {
      const expDateString = faaMaster['EXPIRATION DATE'] // YYYYMMDD

      const year = Number(expDateString.slice(0, 4))
      const month = Number(expDateString.slice(4, 6)) - 1
      const day = Number(expDateString.slice(6, 8))

      this.info.regExpiration = new Date(year, month, day, 0, 0, 0)
    }
  }

  loadFAAACFTREF(faaACF: fAAACFTREF) {
    this.info.make = faaACF.MFR?.trim() ?? ''
    this.info.model = faaACF.MODEL?.trim() ?? ''

    const acType = faaACF['TYPE-ACFT'] as FaaAircraftTypes
    switch (acType) {
      case 1:
        this.info.category = Category.Glider
        break
      case 2:
        this.info.category = Category.LighterThanAir
        this.info.class = AircraftClass.ballg
        break
      case 3:
        this.info.category = Category.LighterThanAir
        this.info.class = AircraftClass.airship
        break
      case 4:
        this.info.category = Category.Airplane

        switch (Number(faaACF['AC-CAT'])) {
          case 1:
          case 2:
          default:
            this.info.class = AircraftClass.sel
            break
        }
        break
      case 5:
        this.info.category = Category.Airplane

        switch (Number(faaACF['AC-CAT'])) {
          case 1:
            this.info.class = AircraftClass.mel
            break
          case 2:
            this.info.class = AircraftClass.mes
            break
          default:
            this.info.class = AircraftClass.mel
            break
        }
        break
      case 6:
        this.info.category = Category.Rotorcraft
        break
      case 7:
        this.info.category = Category.WeightShiftControl
        break
      case 8:
        this.info.category = Category.PoweredParachute
        break
      case 9:
        this.info.category = Category.Rotorcraft
        this.info.class = AircraftClass.gryo
        break
      default:
        this.info.category = Category.Other
        break
    }

    for (let i = 0; i < Number(faaACF['NO-ENG']); i++) {
      this.engines.push({
        model: '',
        serial: '',
        majoroh: 0,
        hpThrust: 0,
        mfr: '',
        year: 0,
        installDate: new Date(1970, 0, 1, 0, 0, 0),
        installTach: 0,
        acInstallTach: 0,
        type: EngineType.Unknown
      })
    }

    if (faaACF['TYPE-ENG'] === '1' || faaACF['TYPE-ENG'] === '2') {
      for (let i = 0; i < Number(faaACF['NO-ENG']); i++) {
        this.props.push(createPropeller())
      }
    }
  }

  loadFAAEngine(faaEngine: fAAEngine) {
    this.engines.forEach((eng) => {
      eng.mfr = faaEngine.MFR
      eng.hpThrust =
        Number(faaEngine.HORSEPOWER) === 0 ? Number(faaEngine.THRUST) : Number(faaEngine.HORSEPOWER)
      eng.model = faaEngine.MODEL
      eng.type = Number(faaEngine.TYPE)
    })
  }

  createCopy(): Aircraft {
    return new Aircraft({
      ...this,
      firebaseInfo: { ...this.firebaseInfo }
    })
  }
}

export const AircraftConverter = {
  toFirestore: (aircraft: Aircraft) => {
    const firebaseInfo = {
      ...aircraft.firebaseInfo,
      _updatedAt: Timestamp.now()
    }

    return {
      numFlights: aircraft.numFlights,
      engines: aircraft.engines,
      props: aircraft.props,
      hubs: aircraft.hubs,
      documents: aircraft.documents,
      squawks: aircraft.squawks,
      maintenanceTechIds: aircraft.maintenanceTechIds,
      info: aircraft.info,
      equipment: aircraft.equipment,
      owners: aircraft.owners,
      requirements: aircraft.requirements,
      cfiRequirements: aircraft.cfiRequirements,
      admin: aircraft.admin,
      maintenanceToDate: aircraft.maintenanceToDate,
      finance: aircraft.finance,
      enableEmailNotifications: aircraft.enableEmailNotifications,
      enablePhoneNotifications: aircraft.enablePhoneNotifications,
      firebaseInfo
    }
  },

  fromFirestore: (
    snapshot: QueryDocumentSnapshot<DocumentData, DocumentData>,
    options?: SnapshotOptions
  ) => {
    const raw = snapshot.data(options)
    const data = convertTimestampsToDates(raw)

    const normalized = {
      ...data,
      owners: (data.owners ?? [data.owner].filter(Boolean)).map((o: any) => createOwner(o)),
      info: createInfo(data.info),
      requirements: createRequirements(data.requirements),
      admin: createAdmin(data.admin),
      equipment: createEquipment(data.equipment),
      maintenanceToDate: createMaintenanceToDate(data.maintenanceToDate),
      finance: createCurrentFinance(data.finance),
      firebaseInfo: {
        ...data.firebaseInfo
      }
    }

    return new Aircraft(normalized)
  }
}
